import type { ReactNode, RefObject, ComponentType } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { MoreHorizontal, X } from "lucide-react";
import { Button } from "@akuna/ui";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export type DocumentViewAction = {
  key: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: "default" | "outline" | "ghost";
  hidden?: boolean;
  /** Renders inside the overflow "more actions" dropdown instead of the primary bar when true */
  secondary?: boolean;
  /** When true and this is a secondary action, renders a DropdownMenuSeparator immediately before it */
  separatorBefore?: boolean;
};

export type DocumentViewDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant: "fullscreen" | "modal";
  title: string;
  statusBadge?: ReactNode;
  subtitle?: string;
  actions: DocumentViewAction[];
  moreActionsLabel: string;
  children: ReactNode;
  printRef?: RefObject<HTMLDivElement>;
};

const renderActionButton = (action: DocumentViewAction, keyPrefix: string) => {
  const Icon = action.icon;
  return (
    <Button
      key={`${keyPrefix}-${action.key}`}
      type="button"
      variant={action.variant ?? "outline"}
      onClick={action.onClick}
      disabled={action.disabled || action.loading}
    >
      {Icon ? <Icon className={`mr-2 h-4 w-4 ${action.loading ? "animate-spin" : ""}`} /> : null}
      {action.label}
    </Button>
  );
};

const renderMenuItem = (action: DocumentViewAction, keyPrefix: string) => {
  const Icon = action.icon;
  return (
    <DropdownMenuItem
      key={`${keyPrefix}-${action.key}`}
      onSelect={action.onClick}
      disabled={action.disabled || action.loading}
    >
      {Icon ? <Icon className={`mr-2 h-4 w-4 ${action.loading ? "animate-spin" : ""}`} /> : null}
      {action.label}
    </DropdownMenuItem>
  );
};

export function DocumentViewDialog({
  open,
  onOpenChange,
  variant,
  title,
  statusBadge,
  subtitle,
  actions,
  moreActionsLabel,
  children,
  printRef,
}: DocumentViewDialogProps) {
  if (!open) return null;

  const visibleActions = actions.filter((action) => !action.hidden);
  const primaryActions = visibleActions.filter((action) => !action.secondary);
  const secondaryActions = visibleActions.filter((action) => action.secondary);

  const body = (
    <div ref={printRef}>
      {children}
    </div>
  );

  const actionBar = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {primaryActions.map((action) => renderActionButton(action, "primary"))}
      {secondaryActions.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline">
              <MoreHorizontal className="mr-2 h-4 w-4" />
              {moreActionsLabel}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {secondaryActions.map((action) => (
              <div key={`${action.key}-wrapper`}>
                {action.separatorBefore ? <DropdownMenuSeparator /> : null}
                {renderMenuItem(action, "secondary")}
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );

  if (variant === "fullscreen") {
    // Rendered through Radix's own Dialog primitives (rather than a plain
    // `createPortal`ed div): a plain `fixed` element positions itself relative
    // to the nearest ancestor with a CSS transform/filter/contain (e.g. an
    // animated Radix DialogContent this component may be nested under), not
    // the viewport, so Radix's own Portal is used to guarantee this shell
    // always actually covers the full screen.
    //
    // Separately: when this view is opened from within an already-open Radix
    // Dialog (e.g. the term/prescription creation dialog stays mounted so its
    // state survives), that ancestor's scroll lock (react-remove-scroll) keeps
    // blocking wheel/touch scrolling on anything outside its own DOM subtree,
    // including this fullscreen shell — the two locks don't hand off cleanly.
    // The scrollable body below stops wheel/touch events from bubbling up to
    // that ancestor's document-level listener, so its lock never sees them and
    // this shell's own native scrolling works regardless of whether it's
    // nested under another open dialog.
    const stopScrollBubble = (event: { stopPropagation: () => void }) => event.stopPropagation();

    return (
      <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Content
            aria-describedby={undefined}
            className="fixed inset-0 z-50 bg-white flex flex-col outline-none"
          >
            <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
            <div className="sticky top-0 z-10 border-b bg-background/95 p-4 backdrop-blur supports-[backdrop-filter]:bg-background/80 flex-shrink-0 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-base font-semibold text-foreground">{title}</h2>
                    {statusBadge}
                  </div>
                  {subtitle ? <p className="truncate text-sm text-muted-foreground">{subtitle}</p> : null}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 rounded-full"
                  onClick={() => onOpenChange(false)}
                  aria-label={moreActionsLabel}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="-mx-4 px-4">{actionBar}</div>
            </div>
            <div
              className="flex-1 overflow-y-auto p-4 min-h-0 bg-print-workspace"
              onWheel={stopScrollBubble}
              onTouchMove={stopScrollBubble}
            >
              {body}
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-4xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {subtitle ? (
            <div className="space-y-1">
              <p className="text-base font-semibold text-foreground">{subtitle}</p>
            </div>
          ) : null}
          <div className="rounded-lg border p-6">{body}</div>
          <DialogFooter className="flex-col sm:flex-row sm:justify-end gap-2">{actionBar}</DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
